// src/components/screens/Reports.js
import {
  Activity,
  AlertCircle,
  BarChart3,
  CheckCircle,
  Clock,
  Download,
  MapPin,
  PieChart,
  RefreshCw,
  Shield,
  Target,
  TrendingUp,
  User,
  Users,
  XCircle
} from 'lucide-react';
import { useEffect, useState } from 'react';
import apiService from '../../services/apiService';

const Reports = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7days');
  const [selectedReport, setSelectedReport] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [officers, setOfficers] = useState([]);
  const [scanLogs, setScanLogs] = useState([]);
  const [reportData, setReportData] = useState(null);

  // Fetch data from APIs
  const fetchReportData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Reports: Fetching data...');

      // Fetch both officers and scan logs
      const [officersResponse, scansResponse] = await Promise.allSettled([
        apiService.getUsers(),
        apiService.getScans()
      ]);

      // Process officers data
      let officersData = [];
      if (officersResponse.status === 'fulfilled' && officersResponse.value?.success) {
        officersData = officersResponse.value.data.filter(user => user.role === 'Officer');
      }

      // Process scans data
      let scansData = [];
      if (scansResponse.status === 'fulfilled' && scansResponse.value?.success) {
        scansData = scansResponse.value.logs || [];
      }

      setOfficers(officersData);
      setScanLogs(scansData);

      // Calculate report analytics
      const analytics = calculateAnalytics(officersData, scansData);
      setReportData(analytics);

      console.log('Reports: Data processed', {
        officers: officersData.length,
        scans: scansData.length,
        analytics
      });

    } catch (error) {
      console.error('Reports: Error fetching data:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate comprehensive analytics
  const calculateAnalytics = (officersData, scansData) => {
    const now = new Date();
    let startDate;

    // Calculate period start date
    switch (selectedPeriod) {
      case '24hours':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90days':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    }

    // Filter scans by period
    const filteredScans = scansData.filter(scan => {
      const scanDate = new Date(scan.createdAt);
      return scanDate >= startDate;
    });

    // Basic metrics
    const totalScans = filteredScans.length;
    const successfulScans = filteredScans.filter(s => s.result === 'SUCCESS').length;
    const failedScans = filteredScans.filter(s => s.result === 'FAILED').length;
    const verifiedScans = filteredScans.filter(s => s.isVerified === true).length;
    const pendingScans = filteredScans.filter(s => s.isVerified === false && !s.rejectedBy).length;
    const rejectedScans = filteredScans.filter(s => s.rejectedBy).length;

    // Officer analytics
    const activeOfficers = officersData.filter(o => o.isActive === true).length;
    const totalOfficers = officersData.length;

    // Calculate scans per officer
    const officerScanCounts = {};
    filteredScans.forEach(scan => {
      const officerId = scan.scannedBy?._id;
      if (officerId) {
        officerScanCounts[officerId] = (officerScanCounts[officerId] || 0) + 1;
      }
    });

    // Enhance officers with scan counts
    const officersWithStats = officersData.map(officer => ({
      ...officer,
      scanCount: officerScanCounts[officer._id] || 0,
      successRate: (() => {
        const officerScans = filteredScans.filter(s => s.scannedBy?._id === officer._id);
        const successCount = officerScans.filter(s => s.result === 'SUCCESS').length;
        return officerScans.length > 0 ? Math.round((successCount / officerScans.length) * 100) : 0;
      })()
    })).sort((a, b) => b.scanCount - a.scanCount);

    const topPerformer = officersWithStats[0];
    const avgScansPerOfficer = totalOfficers > 0 ? Math.round(totalScans / activeOfficers) : 0;

    // Location analytics
    const locationCounts = {};
    filteredScans.forEach(scan => {
      let location = 'Unknown Location';
      if (scan.meta?.location) {
        if (typeof scan.meta.location === 'object' && scan.meta.location.lat) {
          location = `${scan.meta.location.lat}, ${scan.meta.location.lng}`;
        } else {
          location = scan.meta.location;
        }
      } else if (scan.meta?.locationName) {
        location = scan.meta.locationName;
      }
      locationCounts[location] = (locationCounts[location] || 0) + 1;
    });

    const scansByLocation = Object.entries(locationCounts)
      .map(([location, count]) => ({ location, count }))
      .sort((a, b) => b.count - a.count);

    // Scan type analytics
    const qrCodeScans = filteredScans.filter(s => 
      s.code?.toUpperCase().includes('QR') || 
      s.code?.length > 10
    ).length;
    const barcodeScans = totalScans - qrCodeScans;

    // Device analytics
    const deviceCounts = {};
    filteredScans.forEach(scan => {
      const deviceId = scan.meta?.deviceId || 'Unknown Device';
      deviceCounts[deviceId] = (deviceCounts[deviceId] || 0) + 1;
    });

    // Time-based analytics
    const hourlyData = Array(24).fill(0);
    const dailyData = Array(7).fill(0);
    
    filteredScans.forEach(scan => {
      const scanDate = new Date(scan.createdAt);
      hourlyData[scanDate.getHours()]++;
      dailyData[scanDate.getDay()]++;
    });

    // Verification analytics
    const verificationStats = {
      verified: verifiedScans,
      pending: pendingScans,
      rejected: rejectedScans,
      verificationRate: totalScans > 0 ? Math.round((verifiedScans / totalScans) * 100) : 0
    };

    // Verifier performance
    const verifierCounts = {};
    filteredScans.forEach(scan => {
      if (scan.verifiedBy?.email) {
        verifierCounts[scan.verifiedBy.email] = (verifierCounts[scan.verifiedBy.email] || 0) + 1;
      }
    });

    const topVerifiers = Object.entries(verifierCounts)
      .map(([email, count]) => ({ email, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      // Basic metrics
      totalScans,
      successfulScans,
      failedScans,
      successRate: totalScans > 0 ? Math.round((successfulScans / totalScans) * 100) : 0,
      
      // Officer metrics
      activeOfficers,
      totalOfficers,
      topPerformer,
      avgScansPerOfficer,
      officersWithStats,
      
      // Location metrics
      uniqueLocations: scansByLocation.length,
      scansByLocation,
      
      // Scan type metrics
      scansByType: {
        qrCode: qrCodeScans,
        barcode: barcodeScans
      },
      
      // Verification metrics
      verificationStats,
      topVerifiers,
      
      // Device metrics
      uniqueDevices: Object.keys(deviceCounts).length,
      deviceUsage: Object.entries(deviceCounts)
        .map(([device, count]) => ({ device, count }))
        .sort((a, b) => b.count - a.count),
      
      // Time analytics
      hourlyData,
      dailyData,
      peakHour: hourlyData.indexOf(Math.max(...hourlyData)),
      peakDay: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][
        dailyData.indexOf(Math.max(...dailyData))
      ],
      
      // Period info
      periodStartDate: startDate,
      lastUpdated: new Date()
    };
  };

  // Load data on component mount and period change
  useEffect(() => {
    fetchReportData();
  }, []);

  useEffect(() => {
    if (officers.length > 0 || scanLogs.length > 0) {
      const analytics = calculateAnalytics(officers, scanLogs);
      setReportData(analytics);
    }
  }, [selectedPeriod, officers, scanLogs]);

  const handleRefresh = async () => {
    await fetchReportData();
  };

  const handleExportReport = () => {
    if (!reportData) return;

    const exportData = {
      reportType: selectedReport,
      period: selectedPeriod,
      generatedAt: new Date().toISOString(),
      summary: {
        totalScans: reportData.totalScans,
        successRate: reportData.successRate,
        activeOfficers: reportData.activeOfficers,
        uniqueLocations: reportData.uniqueLocations
      },
      detailedData: reportData,
      officers: officers,
      scanLogs: scanLogs
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `report_${selectedReport}_${selectedPeriod}_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const MetricCard = ({ title, value, change, icon: IconComponent, color = 'blue', trend = 'up', delay = 0 }) => (
    <div
      style={{ animationDelay: `${delay}ms` }}
      className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 hover:shadow-lg transition-all duration-300 hover-lift animate-fade-in-up"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {change && (
            <div className={`flex items-center text-sm font-semibold ${
              trend === 'up' ? 'text-emerald-600 dark:text-emerald-400' : 
              trend === 'down' ? 'text-red-500 dark:text-red-400' :
              'text-gray-500 dark:text-gray-400'
            }`}>
              <TrendingUp className={`h-4 w-4 mr-2 ${trend === 'down' ? 'rotate-180' : trend === 'neutral' ? 'rotate-90' : ''}`} />
              <span>{change}</span>
            </div>
          )}
        </div>
        {IconComponent && (
          <div className={`p-3 rounded-xl transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 ${
            color === 'blue' ? 'bg-primary-100 dark:bg-primary-900/50' :
            color === 'green' ? 'bg-green-100 dark:bg-green-900/50' :
            color === 'red' ? 'bg-red-100 dark:bg-red-900/50' :
            color === 'purple' ? 'bg-accent-100 dark:bg-accent-900/50' :
            color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/50' :
            'bg-gray-100 dark:bg-gray-700'
          }`}>
            <IconComponent className={`h-6 w-6 ${
              color === 'blue' ? 'text-primary-600 dark:text-primary-400' :
              color === 'green' ? 'text-green-600 dark:text-green-400' :
              color === 'red' ? 'text-red-600 dark:text-red-400' :
              color === 'purple' ? 'text-accent-600 dark:text-accent-400' :
              color === 'yellow' ? 'text-yellow-600 dark:text-yellow-400' :
              'text-gray-600 dark:text-gray-400'
            }`} />
          </div>
        )}
      </div>
    </div>
  );

  const OverviewReport = () => (
    <div className="space-y-8">
      {/* Key Metrics */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Key Performance Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total Scans"
            value={reportData.totalScans}
            change={`${reportData.totalScans > 0 ? '+' : ''}${reportData.totalScans} this period`}
            icon={Activity}
            color="blue"
          />
          <MetricCard
            title="Success Rate"
            value={`${reportData.successRate}%`}
            change={`${reportData.successfulScans}/${reportData.totalScans} successful`}
            icon={Target}
            color="green"
          />
          <MetricCard
            title="Active Officers"
            value={reportData.activeOfficers}
            change={`${reportData.totalOfficers} total officers`}
            icon={Users}
            color="purple"
          />
          <MetricCard
            title="Locations Covered"
            value={reportData.uniqueLocations}
            change={`${reportData.uniqueDevices} devices used`}
            icon={MapPin}
            color="blue"
          />
        </div>
      </div>

      {/* Verification Metrics */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Verification Analytics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Verification Rate"
            value={`${reportData.verificationStats.verificationRate}%`}
            change={`${reportData.verificationStats.verified} verified`}
            icon={CheckCircle}
            color="green"
          />
          <MetricCard
            title="Pending Verification"
            value={reportData.verificationStats.pending}
            change="Awaiting review"
            icon={Clock}
            color="yellow"
          />
          <MetricCard
            title="Rejected Scans"
            value={reportData.verificationStats.rejected}
            change="Quality control"
            icon={XCircle}
            color="red"
          />
          <MetricCard
            title="Peak Activity"
            value={`${reportData.peakHour}:00`}
            change={`${reportData.peakDay}s most active`}
            icon={BarChart3}
            color="purple"
          />
        </div>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Scan Type Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-bold text-gray-900 dark:text-white">Scan Type Distribution</h4>
            <PieChart className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-accent-50 dark:bg-accent-900/20 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-accent-500 rounded-full"></div>
                <span className="text-gray-900 dark:text-white font-medium">QR Code</span>
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900 dark:text-white">{reportData.scansByType.qrCode}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {reportData.totalScans > 0 ? Math.round((reportData.scansByType.qrCode / reportData.totalScans) * 100) : 0}%
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span className="text-gray-900 dark:text-white font-medium">Barcode</span>
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900 dark:text-white">{reportData.scansByType.barcode}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {reportData.totalScans > 0 ? Math.round((reportData.scansByType.barcode / reportData.totalScans) * 100) : 0}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Locations */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-bold text-gray-900 dark:text-white">Top Scan Locations</h4>
            <MapPin className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="space-y-3">
            {reportData.scansByLocation.slice(0, 5).map((location, index) => (
              <div key={location.location} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
                    index === 0 ? 'bg-yellow-500' :
                    index === 1 ? 'bg-gray-400' :
                    index === 2 ? 'bg-orange-500' :
                    'bg-primary-500'
                  }`}>
                    {index + 1}
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white truncate max-w-40">
                    {location.location}
                  </span>
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900 dark:text-white">{location.count}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {reportData.totalScans > 0 ? Math.round((location.count / reportData.totalScans) * 100) : 0}%
                  </div>
                </div>
              </div>
            ))}
            {reportData.scansByLocation.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No location data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Verifiers */}
      {reportData.topVerifiers.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-bold text-gray-900 dark:text-white">Top Verifiers</h4>
            <Shield className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {reportData.topVerifiers.map((verifier, index) => (
              <div key={verifier.email} className="text-center p-4 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl border border-primary-200/50 dark:border-primary-700/50">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <User className="h-6 w-6 text-white" />
                </div>
                <p className="text-sm font-medium text-primary-900 dark:text-primary-100 truncate">
                  {verifier.email}
                </p>
                <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                  {verifier.count} verifications
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const PerformanceReport = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Officer Performance Ranking</h3>
        <div className="space-y-4">
          {reportData.officersWithStats.length > 0 ? (
            reportData.officersWithStats.map((officer, index) => (
              <div key={officer._id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                    index === 0 ? 'bg-yellow-500' :
                    index === 1 ? 'bg-gray-400' :
                    index === 2 ? 'bg-orange-500' :
                    'bg-primary-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-600 rounded-xl flex items-center justify-center">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {officer.firstName} {officer.lastName}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {officer.accessCode} • {officer.email}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900 dark:text-white">
                    {officer.scanCount.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {officer.successRate}% success rate
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No officer performance data available
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const VerificationReport = () => (
    <div className="space-y-6">
      {/* Verification Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <h4 className="text-lg font-bold text-gray-900 dark:text-white">Verified</h4>
              <p className="text-2xl font-bold text-green-600">{reportData.verificationStats.verified}</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {reportData.verificationStats.verificationRate}% of total scans
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="h-8 w-8 text-yellow-500" />
            <div>
              <h4 className="text-lg font-bold text-gray-900 dark:text-white">Pending</h4>
              <p className="text-2xl font-bold text-yellow-600">{reportData.verificationStats.pending}</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Awaiting verification
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <XCircle className="h-8 w-8 text-red-500" />
            <div>
              <h4 className="text-lg font-bold text-gray-900 dark:text-white">Rejected</h4>
              <p className="text-2xl font-bold text-red-600">{reportData.verificationStats.rejected}</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Quality control rejections
          </p>
        </div>
      </div>

      {/* Verification Performance */}
      {reportData.topVerifiers.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
          <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Verifier Performance</h4>
          <div className="space-y-4">
            {reportData.topVerifiers.map((verifier, index) => (
              <div key={verifier.email} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-primary-600 rounded-xl flex items-center justify-center">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{verifier.email}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Verifier</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900 dark:text-white">{verifier.count}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">verifications</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const SystemReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* System Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
          <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">System Performance</h4>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Total Users</span>
              <span className="font-bold text-gray-900 dark:text-white">{reportData.totalOfficers}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Active Officers</span>
              <span className="font-bold text-green-600">{reportData.activeOfficers}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Peak Activity Hour</span>
              <span className="font-bold text-primary-600">{reportData.peakHour}:00</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Most Active Day</span>
              <span className="font-bold text-accent-600">{reportData.peakDay}</span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-gray-600 dark:text-gray-400">Unique Devices</span>
              <span className="font-bold text-gray-900 dark:text-white">{reportData.uniqueDevices}</span>
            </div>
          </div>
        </div>

        {/* Device Usage */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
          <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Device Usage Statistics</h4>
          <div className="space-y-3">
            {reportData.deviceUsage.slice(0, 5).map((device, index) => (
              <div key={device.device} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
                    index === 0 ? 'bg-primary-500' :
                    index === 1 ? 'bg-green-500' :
                    index === 2 ? 'bg-accent-500' :
                    'bg-gray-500'
                  }`}>
                    {index + 1}
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white truncate max-w-32">
                    {device.device}
                  </span>
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900 dark:text-white">{device.count}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {reportData.totalScans > 0 ? Math.round((device.count / reportData.totalScans) * 100) : 0}%
                  </div>
                </div>
              </div>
            ))}
            {reportData.deviceUsage.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No device usage data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Time Analytics */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6">
        <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Activity Patterns</h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Hourly Distribution */}
          <div>
            <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Hourly Distribution</h5>
            <div className="space-y-2">
              {reportData.hourlyData.map((count, hour) => {
                const maxCount = Math.max(...reportData.hourlyData);
                const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                return (
                  <div key={hour} className="flex items-center space-x-3">
                    <span className="text-sm font-mono text-gray-600 dark:text-gray-400 w-8">
                      {hour.toString().padStart(2, '0')}h
                    </span>
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-8">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Daily Distribution */}
          <div>
            <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Daily Distribution</h5>
            <div className="space-y-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                const count = reportData.dailyData[index];
                const maxCount = Math.max(...reportData.dailyData);
                const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                return (
                  <div key={day} className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-8">
                      {day}
                    </span>
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div 
                        className="bg-green-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-8">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderReport = () => {
    if (!reportData) return null;
    
    switch (selectedReport) {
      case 'overview':
        return <OverviewReport />;
      case 'performance':
        return <PerformanceReport />;
      case 'verification':
        return <VerificationReport />;
      case 'system':
        return <SystemReport />;
      default:
        return <OverviewReport />;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="skeleton h-8 w-64"></div>
            <div className="skeleton h-4 w-80"></div>
          </div>
          <div className="skeleton h-10 w-32 rounded-xl"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 space-y-3">
              <div className="skeleton h-4 w-24"></div>
              <div className="skeleton h-8 w-16"></div>
              <div className="skeleton h-3 w-32"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">Error loading reports: {error}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            Reports & Analytics
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Comprehensive insights and performance analytics
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="group px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 transition-all duration-200 disabled:opacity-50 hover:scale-[1.03] active:scale-[0.98]"
          >
            <RefreshCw className={`h-4 w-4 transition-transform duration-500 ${isLoading ? 'animate-spin' : 'group-hover:rotate-180'}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleExportReport}
            className="px-4 py-2 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-xl hover:from-primary-600 hover:to-accent-700 flex items-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.03] active:scale-[0.98]"
          >
            <Download className="h-4 w-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 animate-fade-in-up" style={{ animationDelay: '80ms' }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex flex-wrap gap-4">
            {/* Report Type Selection */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Report Type:</label>
              <select
                value={selectedReport}
                onChange={(e) => setSelectedReport(e.target.value)}
                className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
              >
                <option value="overview">Overview</option>
                <option value="performance">Officer Performance</option>
                <option value="verification">Verification Analytics</option>
                <option value="system">System Analytics</option>
              </select>
            </div>

            {/* Time Period Selection */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Period:</label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
              >
                <option value="24hours">Last 24 Hours</option>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-3 text-sm text-gray-500 dark:text-gray-400">
            <Clock className="h-4 w-4" />
            <span>Last updated: {reportData?.lastUpdated?.toLocaleString() || 'Never'}</span>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {renderReport()}
    </div>
  );
};

export default Reports;